import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, X, Download, Eye, RefreshCw, Palette, Grid2x2 as Grid, Image as ImageIcon, Minus, Plus, Store, ArrowLeft, Loader, CheckCircle, AlertCircle, ImagePlus } from 'lucide-react';
import { removeBackgroundFromBlob } from '../services/removeBackgroundService';
import { LoadingIndicator } from './LoadingIndicator';
import { ImageControls } from './ImageControls';
import { ColorPickerModal } from './ColorPickerModal';
import { PreviewModal } from './PreviewModal';
import { PushToShopifyModal } from './PushToShopifyModal';
import { useUserPlan } from '../hooks/useUserPlan';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { ADMIN_EMAIL } from '../lib/constants';
import { useIsShopifyUser } from '../hooks/useIsShopifyUser';
import { FeedbackWidget } from './FeedbackWidget';
import { WatermarkUpgradeBanner } from './WatermarkUpgradeBanner';
import { LockedDownloadButton } from './LockedDownloadButton';
import { ToolPageHero } from './ToolPageHero';
import { ToolFAQ } from './ToolFAQ';
import { ToolAboutSection, ToolDemoVideo, RelatedTools, type ToolPageCopy } from './ToolPageSections';
import { renderCanvasPreview } from '../utils/canvasPreview';
import { applyWatermark, drawWatermarkBadge } from '../utils/watermarkImage';
import { VideoModal } from './VideoModal';
import { triggerConfetti } from '../utils/confetti';
import JSZip from 'jszip';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { ShareBeforeAfterButton } from './ShareBeforeAfter';

type BackgroundOption = 'transparent' | 'white' | 'black' | 'gradient' | 'custom' | 'blue' | 'yellow' | 'green' | string;

interface BackgroundSwatch {
  type: BackgroundOption;
  label: string;
  color?: string;
  className?: string;
  icon?: React.ReactNode;
  url?: string;
}

const BACKGROUND_SWATCHES: BackgroundSwatch[] = [
  { type: 'transparent', label: 'Transparent', className: 'bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+CjxyZWN0IHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI2YxZjFmMSI+PC9yZWN0Pgo8cmVjdCB4PSIxMCIgeT0iMTAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI2YxZjFmMSI+PC9yZWN0Pgo8cmVjdCB4PSIxMCIgeT0iMCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZTVlNWU1Ij48L3JlY3Q+CjxyZWN0IHg9IjAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNlNWU1ZTUiPjwvcmVjdD4KPC9zdmc+)]' },
  { type: 'white', label: 'White', color: '#ffffff' },
  { type: 'black', label: 'Black', color: '#000000' },
  { type: 'light_gray', label: 'Light Gray', color: '#F2F2F2' },
  { type: 'platinum', label: 'Platinum', color: '#E5E4E2' },
  { type: 'gradient', label: 'Gradient', className: 'bg-gradient-to-t from-black to-white' },
  { type: 'custom', label: 'Custom Color', icon: <Palette className="w-5 h-5 text-gray-700" /> },
  // Textured backgrounds with Pexels links
  { type: 'texture_tiles', label: 'Tiles', url: 'https://images.pexels.com/photos/4790056/pexels-photo-4790056.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2' },
  { type: 'texture_stucco', label: 'Stucco', url: 'https://images.pexels.com/photos/11236688/pexels-photo-11236688.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2' },
  { type: 'texture_paper', label: 'Paper', url: 'https://images.pexels.com/photos/20899958/pexels-photo-20899958.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2' },
  // Additional textured backgrounds
  { type: 'texture_wood', label: 'Wood', url: 'https://images.pexels.com/photos/129731/pexels-photo-129731.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2' },
  { type: 'texture_teal', label: 'Teal Wall', url: 'https://images.pexels.com/photos/304664/pexels-photo-304664.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2' },
  { type: 'texture_snow', label: 'Snow', url: 'https://images.pexels.com/photos/4709467/pexels-photo-4709467.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2' },
  { type: 'custom_url', label: 'Custom URL', icon: <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg> },
];

interface ProcessedImage {
  name: string;
  originalBlob: Blob;
  originalUrl: string;
  processedBlob: Blob | null;
  processedUrl: string | null;
  processing: boolean;
  error?: string;
  rotation: number;
  editState?: {
    brightness: number;
    contrast: number;
    saturation: number;
    hue: number;
    rotation: number;
    backgroundColor?: string;
    shadowType: 'none' | 'medium' | 'hard' | 'floating' | 'contact' | 'reflection';
    centerProduct: boolean;
    autoCrop: boolean;
    sharpness: number;
    paddingAmount: number;
  };
}

interface EditState {
  removeBackground: boolean;
  brightness: number;
  contrast: number;
  saturation: number;
  hue: number;
  selectedBackground: BackgroundOption;
  customColor: string;
  customBackgroundUrl: string;
  shadowType: 'none' | 'soft' | 'medium' | 'hard' | 'floating' | 'contact' | 'reflection';
  centerProduct: boolean;
  autoCrop: boolean;
  sharpness: number;
  paddingAmount: number;
}

const defaultEditState: EditState = {
  removeBackground: true,
  brightness: 100,
  contrast: 100,
  saturation: 100,
  hue: 0,
  selectedBackground: 'white',
  customColor: '#000000',
  customBackgroundUrl: '',
  shadowType: 'none',
  centerProduct: false,
  autoCrop: true,
  sharpness: 50,
  paddingAmount: 20
};

const MAX_IMAGES = 50;

export function BulkBackgroundRemover() {
  const [images, setImages] = useState<ProcessedImage[]>([]);
  const [editState, setEditState] = useState<EditState>(defaultEditState);
  const [history, setHistory] = useState<EditState[]>([defaultEditState]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [isUrlInputOpen, setIsUrlInputOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<ProcessedImage | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageCopy, setPageCopy] = useState<ToolPageCopy | null>(null);
  const [modalVideoUrl, setModalVideoUrl] = useState('https://youtu.be/OHTJFPWfUxg');
  const [downloadProgress, setDownloadProgress] = useState('');
  const [shopifyProductIds, setShopifyProductIds] = useState<Map<string, { productId: string; productTitle: string; imageId: string }>>(new Map());
  const [fromShopify, setFromShopify] = useState(false);
  const [loadingShopifyImages, setLoadingShopifyImages] = useState(false);
  const [pushToShopifyImage, setPushToShopifyImage] = useState<{ url?: string; base64?: string; productId?: string; productTitle?: string } | null>(null);
  const [shopifyPushImages, setShopifyPushImages] = useState<{ name: string; base64: string; productId: string; productTitle: string; imageId: string }[]>([]);
  const [showShopifyPushConfirm, setShowShopifyPushConfirm] = useState(false);
  const [shopifyPushing, setShopifyPushing] = useState(false);
  const [shopifyPushResult, setShopifyPushResult] = useState<{ success: number; failed: number } | null>(null);
  const [shopifyPushProgress, setShopifyPushProgress] = useState('');
  const [shopifyProductDetails, setShopifyProductDetails] = useState<Record<string, { title: string; images: { id: string; url: string }[] }>>({});
  const [shopifyReplaceChoices, setShopifyReplaceChoices] = useState<Record<number, string | null>>({});
  const [loadingProductDetails, setLoadingProductDetails] = useState(false);
  const { plan, checkCredits, refreshPlan } = useUserPlan();
  const navigate = useNavigate();
  const location = useLocation();
  const supabase = useSupabaseClient();
  const session = useSession();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRefs = useRef<{ [key: string]: HTMLCanvasElement }>({});
  const processingRef = useRef(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const pageCopyRef = useRef<HTMLDivElement>(null);
  const isAdmin = session?.user?.email === ADMIN_EMAIL;
  const { isShopifyUser, isLoading: shopifyLoading } = useIsShopifyUser();
  const downloadLocked = !!plan && !plan.has_made_purchase && !isShopifyUser && !shopifyLoading;

  useEffect(() => {
    const state = location.state as { shopifyImages?: { url: string; productId: string; productTitle: string; imageId: string }[]; fromShopify?: boolean } | null;
    if (!state?.shopifyImages || !state.fromShopify) return;

    setFromShopify(true);
    setLoadingShopifyImages(true);

    async function loadShopifyImages() {
      const shopifyImgs = state!.shopifyImages!;
      const loaded: ProcessedImage[] = [];
      const productIdMap = new Map<string, { productId: string; productTitle: string; imageId: string }>();

      for (const img of shopifyImgs) {
        try {
          const response = await fetch(img.url);
          const blob = await response.blob();
          const extension = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
          const fileName = `shopify-${img.imageId}.${extension}`;
          const url = URL.createObjectURL(blob);
          loaded.push({
            name: fileName,
            originalBlob: blob,
            originalUrl: url,
            processedBlob: null,
            processedUrl: null,
            processing: false,
            rotation: 0,
          });
          productIdMap.set(fileName, { productId: img.productId, productTitle: img.productTitle, imageId: img.imageId });
        } catch {
          // Skip images that fail to download
        }
      }

      if (loaded.length > 0) {
        setImages(loaded);
        setShopifyProductIds(productIdMap);
      }
      setLoadingShopifyImages(false);
    }

    loadShopifyImages();
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    async function fetchPageCopy() {
      try {
        const { data, error } = await supabase
          .from('page_copy')
          .select('copy')
          .eq('id', 'background_remover')
          .single();

        if (error) throw error;
        if (data?.copy) {
          setPageCopy(data.copy);
        }
      } catch (err) {
        console.error('Error fetching page copy:', err);
      }
    }

    fetchPageCopy();
  }, [supabase]);

  // Event delegation for demo links in page copy
  useEffect(() => {
    const handleDemoLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if the clicked element or any parent is a demo link
      const demoLink = target.closest('a[id="background-remover-subcopy-demo-link"]') as HTMLAnchorElement;

      if (demoLink) {
        event.preventDefault();
        const videoUrl = demoLink.getAttribute('href');
        if (videoUrl) {
          setModalVideoUrl(videoUrl);
          setIsVideoModalOpen(true);
        }
      }
    };

    const pageCopyElement = pageCopyRef.current;
    if (pageCopyElement) {
      pageCopyElement.addEventListener('click', handleDemoLinkClick);
    }

    return () => {
      if (pageCopyElement) {
        pageCopyElement.removeEventListener('click', handleDemoLinkClick);
      }
    };
  }, [pageCopy]); // Re-run when pageCopy changes

  // Close color picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (isColorPickerOpen && colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setIsColorPickerOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorPickerOpen]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files
      .filter(file => file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024)
      .slice(0, MAX_IMAGES - images.length);

    if (files.length > MAX_IMAGES - images.length) {
      setError(`You can only upload up to ${MAX_IMAGES} images at a time. ${MAX_IMAGES - images.length} slots remaining.`);
    }

    setEditState(defaultEditState);
    setHistory([defaultEditState]);
    setCurrentHistoryIndex(0);

    const newImages: ProcessedImage[] = validFiles.map(file => ({
      name: file.name,
      originalBlob: file,
      originalUrl: URL.createObjectURL(file),
      processedBlob: null,
      processedUrl: null,
      processing: false,
      rotation: 0
    }));

    setImages(prev => [...prev, ...newImages]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [images.length]);

  const updateEditState = (newState: EditState) => {
    setEditState(newState);
    const newHistory = history.slice(0, currentHistoryIndex + 1);
    newHistory.push(newState);
    setHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = useCallback(() => {
    if (currentHistoryIndex > 0) {
      const newIndex = currentHistoryIndex - 1;
      setCurrentHistoryIndex(newIndex);
      setEditState(history[newIndex]);
    }
  }, [currentHistoryIndex, history]);

  const handleRedo = useCallback(() => {
    if (currentHistoryIndex < history.length - 1) {
      const newIndex = currentHistoryIndex + 1;
      setCurrentHistoryIndex(newIndex);
      setEditState(history[newIndex]);
    }
  }, [currentHistoryIndex, history]);

  useEffect(() => {
    let cancelled = false;
    const CONCURRENCY_LIMIT = 3;

    const processImagesRealtime = async () => {
      if (!editState.removeBackground) {
        return;
      }

      const toProcess: number[] = [];
      for (let i = 0; i < images.length; i++) {
        if (!images[i].processedBlob && !images[i].processing) {
          toProcess.push(i);
        }
      }

      const processSingle = async (i: number) => {
        if (cancelled) return;

        if (!session?.access_token) {
          setError('Please sign in to use background removal.');
          return;
        }

        setImages(prev => prev.map((img, index) =>
          index === i ? { ...img, processing: true } : img
        ));

        try {
          const result = await removeBackgroundFromBlob(images[i].originalBlob, session.access_token);
          if (cancelled) {
            URL.revokeObjectURL(result.url);
            return;
          }

          setImages(prev => prev.map((img, index) =>
            index === i ? {
              ...img,
              processedBlob: result.blob,
              processedUrl: result.url,
              processing: false
            } : img
          ));
        } catch (error) {
          if (cancelled) return;

          setImages(prev => prev.map((img, index) =>
            index === i ? {
              ...img,
              processing: false,
              error: error instanceof Error ? error.message : 'Processing failed'
            } : img
          ));
        }
      };

      let active = 0;
      let nextIdx = 0;

      await new Promise<void>((resolve) => {
        const startNext = () => {
          while (active < CONCURRENCY_LIMIT && nextIdx < toProcess.length) {
            if (cancelled) { resolve(); return; }
            const idx = toProcess[nextIdx++];
            active++;
            processSingle(idx).finally(() => {
              active--;
              if (nextIdx >= toProcess.length && active === 0) {
                resolve();
              } else {
                startNext();
              }
            });
          }
          if (toProcess.length === 0 || (nextIdx >= toProcess.length && active === 0)) {
            resolve();
          }
        };
        startNext();
      });
    };

    processImagesRealtime();

    return () => { cancelled = true; };
  }, [editState.removeBackground, images.length, session?.access_token]);

  const downloadAll = async () => {
    if (downloadLocked) return;
    if (!plan) {
      navigate('/login');
      return;
    }

    if (processingRef.current) {
      return;
    }

    setError(null);
    setDownloading(true);
    setDownloadProgress('');
    processingRef.current = true;

    try {
      const creditsNeeded = images.length;
      if (plan.credits < creditsNeeded) {
        throw new Error(
          plan.credits === 0
            ? `You've used all your credits. Please upgrade your plan to continue.`
            : `Insufficient credits. You need ${creditsNeeded} credits but have ${plan.credits} remaining.`
        );
      }
      const success = await checkCredits(creditsNeeded);
      if (!success) {
        throw new Error('Failed to deduct credits. You may have insufficient credits.');
      }

      const zip = new JSZip();
      const total = images.length;

      const processImage = async (image: ProcessedImage, index: number) => {
        const canvas = document.createElement('canvas');
        const imageUrl = editState.removeBackground && image.processedUrl
          ? image.processedUrl
          : image.originalUrl;

        const imageElement = document.createElement('img');
        await new Promise((resolve, reject) => {
          imageElement.onload = resolve;
          imageElement.onerror = reject;
          imageElement.src = imageUrl;
        });

        setDownloadProgress(`Optimizing image ${index + 1} of ${total}...`);

        canvas.width = imageElement.width;
        canvas.height = imageElement.height;

        await renderCanvasPreview(canvas, imageUrl, {
          brightness: editState.brightness,
          contrast: editState.contrast,
          saturation: editState.saturation,
          hue: editState.hue,
          rotation: image.rotation,
          backgroundColor: getBackgroundValue(editState.selectedBackground, editState.customColor),
          shadowType: editState.shadowType,
          centerProduct: editState.centerProduct,
          autoCrop: editState.autoCrop,
          sharpness: editState.sharpness,
          paddingAmount: editState.paddingAmount,
        });

        const baseName = image.name.replace(/\.[^/.]+$/, '');

        let finalBlob: Blob = await new Promise((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create blob'));
          }, 'image/png');
        });

        if (!plan.has_made_purchase && !isShopifyUser) {
          finalBlob = await applyWatermark(finalBlob);
        }

        return { name: `${baseName}.png`, data: await finalBlob.arrayBuffer() };
      };

      for (let i = 0; i < images.length; i++) {
        const result = await processImage(images[i], i);
        zip.file(result.name, result.data);
      }

      setDownloadProgress('Creating ZIP...');

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'edited-images.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      await refreshPlan();
      triggerConfetti();
      handleNewImages();

    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to download images');
    } finally {
      setDownloading(false);
      setDownloadProgress('');
      processingRef.current = false;
    }
  };

  const revokeImageUrls = (image: ProcessedImage) => {
    URL.revokeObjectURL(image.originalUrl);
    if (image.processedUrl) URL.revokeObjectURL(image.processedUrl);
  };

  const removeImage = (index: number) => {
    const removed = images[index];
    if (removed) revokeImageUrls(removed);
    setImages(prev => prev.filter((_, i) => i !== index));
    const newRefs = { ...previewCanvasRefs.current };
    delete newRefs[index];
    previewCanvasRefs.current = newRefs;
  };

  const handleResetEdits = () => {
    setEditState(defaultEditState);
    setHistory([defaultEditState]);
    setCurrentHistoryIndex(0);
    setImages(prev => prev.map(img => ({ ...img, rotation: 0 })));
  };

  const handleImageRotation = (index: number, delta: number) => {
    setImages(prev => prev.map((img, i) => {
      if (i !== index) return img;
      let newRotation = img.rotation + delta;
      if (newRotation > 180) newRotation = -180 + (newRotation - 180);
      if (newRotation < -180) newRotation = 180 + (newRotation + 180);
      return { ...img, rotation: newRotation };
    }));
  };

  const handleImageRotationReset = (index: number) => {
    setImages(prev => prev.map((img, i) =>
      i === index ? { ...img, rotation: 0 } : img
    ));
  };

  const handleNewImages = () => {
    images.forEach(revokeImageUrls);
    setImages([]);
    setEditState(defaultEditState);
    setHistory([defaultEditState]);
    setCurrentHistoryIndex(0);
    setDownloading(false);
    setIsColorPickerOpen(false);
    setPreviewImage(null);
    setError(null);
    previewCanvasRefs.current = {};
  };

  const fetchShopifyProductDetails = async (images: typeof shopifyPushImages) => {
    setLoadingProductDetails(true);
    const details: typeof shopifyProductDetails = {};
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess?.access_token) return;
      const uniqueProductIds = [...new Set(images.map(img => img.productId))];
      for (const pid of uniqueProductIds) {
        try {
          const gid = pid.startsWith('gid://') ? pid : `gid://shopify/Product/${pid}`;
          const resp = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-fetch-products?productId=${encodeURIComponent(gid)}`,
            { headers: { 'Authorization': `Bearer ${authSess.access_token}`, 'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY } }
          );
          const data = await resp.json();
          if (resp.ok && data.products?.[0]) {
            details[pid] = { title: data.products[0].title, images: data.products[0].images };
          }
        } catch { /* skip */ }
      }
    } catch { /* skip */ } finally {
      setShopifyProductDetails(details);
      setLoadingProductDetails(false);
    }
  };

  const handleMultiShopifyPush = async () => {
    if (shopifyPushImages.length === 0) return;
    setShopifyPushing(true);
    setShopifyPushResult(null);

    const { data: { session: authSess } } = await supabase.auth.getSession();
    if (!authSess?.access_token) {
      setShopifyPushing(false);
      return;
    }

    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < shopifyPushImages.length; i++) {
      const img = shopifyPushImages[i];
      setShopifyPushProgress(`Pushing image ${i + 1} of ${shopifyPushImages.length}...`);
      try {
        const payload: Record<string, unknown> = {
          productId: img.productId,
          imageBase64: img.base64,
        };
        const replaceId = shopifyReplaceChoices[i];
        if (replaceId) {
          payload.replaceImageId = replaceId;
          const productImages = shopifyProductDetails[img.productId]?.images;
          const matchedImg = productImages?.find((pi) => pi.id === replaceId);
          if (matchedImg) payload.replaceImageUrl = matchedImg.url;
        }

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-sync-image`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authSess.access_token}`,
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify(payload),
          }
        );
        if (response.ok) successCount++;
        else failedCount++;
      } catch {
        failedCount++;
      }
    }

    setShopifyPushing(false);
    setShopifyPushProgress('');
    setShopifyPushResult({ success: successCount, failed: failedCount });
  };

  const getBackgroundValue = (background: BackgroundOption, customColor: string): string | undefined => {
    if (background === 'custom') return customColor;
    if (background === 'custom_url') return editState.customBackgroundUrl;
    if (background === 'gradient') return 'gradient';

    const swatch = BACKGROUND_SWATCHES.find(s => s.type === background);
    if (swatch?.url) return swatch.url; // Return URL for textured backgrounds
    if (swatch?.color) return swatch.color;

    return undefined;
  };

  useEffect(() => {
    images.forEach(async (image, index) => {
      if (previewCanvasRefs.current[index]) {
        const backgroundColor = getBackgroundValue(editState.selectedBackground, editState.customColor);
        const imageToRender = (editState.removeBackground && image.processedUrl) ? image.processedUrl : image.originalUrl;
        const canvas = previewCanvasRefs.current[index];

        await renderCanvasPreview(
          canvas,
          imageToRender,
          {
            brightness: editState.brightness,
            contrast: editState.contrast,
            saturation: editState.saturation,
            hue: editState.hue,
            rotation: image.rotation,
            backgroundColor,
            shadowType: editState.shadowType,
            centerProduct: editState.centerProduct,
            autoCrop: editState.autoCrop,
            sharpness: editState.sharpness,
            paddingAmount: editState.paddingAmount
          }
        );

        if (plan && !plan.has_made_purchase && !isShopifyUser) {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            drawWatermarkBadge(ctx, canvas.width, canvas.height);
          }
        }
      }
    });
  }, [images, editState, plan]);

  const getContainerBackgroundClass = () => {
    const swatch = BACKGROUND_SWATCHES.find(s => s.type === editState.selectedBackground);
    if (swatch?.url) return ''; // No Tailwind class needed for image URLs, handled by style
    if (swatch?.className) return swatch.className;

    return editState.selectedBackground === 'transparent'
      ? 'bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+CjxyZWN0IHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI2YxZjFmMSI+PC9yZWN0Pgo8cmVjdCB4PSIxMCIgeT0iMTAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI2YxZjFmMSI+PC9yZWN0Pgo8cmVjdCB4PSIxMCIgeT0iMCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZTVlNWU1Ij48L3JlY3Q+CjxyZWN0IHg9IjAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNlNWU1ZTUiPjwvcmVjdD4KPC9zdmc+)]'
      : '';
  };

  const getContainerStyle = () => {
    const styles: React.CSSProperties = {};

    if (editState.selectedBackground === 'gradient') {
      return {};
    }

    const swatch = BACKGROUND_SWATCHES.find(s => s.type === editState.selectedBackground);

    if (swatch?.url) { // For image URLs, set background image directly
      styles.backgroundImage = `url(${swatch.url})`;
      styles.backgroundSize = 'cover';
      styles.backgroundPosition = 'center';
      styles.backgroundRepeat = 'no-repeat';
      return styles;
    }

    if (editState.selectedBackground === 'custom') {
      styles.backgroundColor = editState.customColor;
    } else if (swatch?.color) {
      styles.backgroundColor = swatch.color;
    }

    return styles;
  };

  const handleBackgroundSelect = (background: BackgroundOption) => {
    updateEditState({ ...editState, selectedBackground: background });
    if (background === 'custom') {
      setIsColorPickerOpen(true);
    } else if (background === 'custom_url') {
      setIsUrlInputOpen(true);
    }
  };

  const handlePreview = (image: ProcessedImage, index?: number) => {
    const imageIndex = index !== undefined ? index : images.findIndex(img => img.name === image.name);
    setPreviewIndex(imageIndex);
    setPreviewImage({
      ...image,
      editState: {
        brightness: editState.brightness,
        contrast: editState.contrast,
        saturation: editState.saturation,
        hue: editState.hue,
        rotation: image.rotation,
        backgroundColor: getBackgroundValue(editState.selectedBackground, editState.customColor),
        shadowType: editState.shadowType,
        centerProduct: editState.centerProduct,
        autoCrop: editState.autoCrop,
        sharpness: editState.sharpness,
        paddingAmount: editState.paddingAmount
      }
    });
  };

  const getPreviewImageUrl = (image: ProcessedImage | null): string => {
    if (!image) return '';
    if (editState.removeBackground && image.processedUrl) return image.processedUrl;
    return image.originalUrl;
  };

  const renderShareAfter = async (image: ProcessedImage): Promise<HTMLCanvasElement> => {
    const canvas = document.createElement('canvas');
    await renderCanvasPreview(
      canvas,
      getPreviewImageUrl(image),
      {
        brightness: editState.brightness,
        contrast: editState.contrast,
        saturation: editState.saturation,
        hue: editState.hue,
        rotation: image.rotation,
        backgroundColor: getBackgroundValue(editState.selectedBackground, editState.customColor),
        shadowType: editState.shadowType,
        centerProduct: editState.centerProduct,
        autoCrop: editState.autoCrop,
        sharpness: editState.sharpness,
        paddingAmount: editState.paddingAmount
      }
    );
    return canvas;
  };

  const handlePreviewPrevious = () => {
    if (previewIndex > 0) {
      handlePreview(images[previewIndex - 1], previewIndex - 1);
    }
  };

  const handlePreviewNext = () => {
    if (previewIndex < images.length - 1) {
      handlePreview(images[previewIndex + 1], previewIndex + 1);
    }
  };

  const isSingleImage = images.length === 1;

  return (
    <>
    <ToolPageHero
      toolName="Background Remover"
      icon={ImageIcon}
      pageCopy={pageCopy}
      plan={plan}
      decorationPreset="minimal"
      gradientClasses="from-emerald-50/25 via-white to-transparent"
      badgeClasses="bg-gray-100 text-gray-700"
      beforeImage={pageCopy?.beforeImage}
      afterImage={pageCopy?.afterImage}
      beforeImageAlt={pageCopy?.beforeImageAlt}
      afterImageAlt={pageCopy?.afterImageAlt}
      subtitleRef={pageCopyRef}
    />
    <div className="container mx-auto px-4 pt-4 pb-8 max-w-6xl">

      {fromShopify && (isAdmin || isShopifyUser) && (
        <div className="mb-6 flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          {loadingShopifyImages ? (
            <Loader className="w-4 h-4 text-green-700 flex-shrink-0 animate-spin" />
          ) : (
            <Store className="w-4 h-4 text-green-700 flex-shrink-0" />
          )}
          <span className="text-sm text-green-800 font-medium">
            {loadingShopifyImages
              ? 'Loading images from Shopify...'
              : `${images.length} image${images.length !== 1 ? 's' : ''} loaded from Shopify`}
          </span>
          <button
            onClick={() => navigate('/connect-shopify')}
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-green-700 hover:text-green-900 font-medium transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Store
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-secondary-50 text-secondary-700 rounded-md">
          {error}
        </div>
      )}

      {!images.length ? (
        loadingShopifyImages ? (
          <div className="aspect-video bg-gray-50 rounded-2xl flex flex-col items-center justify-center">
            <Loader className="w-8 h-8 text-gray-400 animate-spin mb-3" />
            <p className="text-sm font-medium text-gray-700">Loading Shopify images...</p>
            <p className="text-xs text-gray-400 mt-1">This may take a moment</p>
          </div>
        ) : (
        <div className="aspect-video bg-gray-100 rounded-2xl transition-all duration-200">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="bulk-file-input"
            ref={fileInputRef}
          />
          <label
            htmlFor="bulk-file-input"
            className="h-full flex flex-col items-center justify-center cursor-pointer p-8"
          >
            <div className="p-4 rounded-full bg-gray-100">
              <Upload className="h-8 w-8 text-gray-900" />
            </div>
            <div className="text-center">
              <span className="px-6 py-2 bg-primary-600 text-white rounded-full text-sm font-medium hover:bg-primary-700 cursor-pointer transition-all duration-200">
                Choose Files
              </span>
            </div>
            <p className="text-gray-400 text-xs mt-4">
              Supports multiple images up to 10MB each
            </p>
          </label>
        </div>
        )
      ) : (
        <div className="space-y-8">
          {/* Main content area with grid layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left side: Image grid */}
            <div className={`lg:col-span-2 ${isSingleImage ? "max-w-3xl mx-auto" : ""}`}>
              <div className={isSingleImage ? "w-full" : "grid grid-cols-2 sm:grid-cols-3 gap-6"}>
                {images.map((image, index) => (
                  <div key={index} className={`relative ${isSingleImage ? "w-full" : ""}`}>
                    <div
                      className={`${isSingleImage ? "" : "aspect-square"} rounded-lg overflow-hidden flex items-center justify-center border border-dashed border-gray-200 ${
                        editState.removeBackground && editState.selectedBackground === 'transparent'
                          ? 'bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+CjxyZWN0IHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI2YxZjFmMSI+PC9yZWN0Pgo8cmVjdCB4PSIxMCIgeT0iMTAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI2YxZjFmMSI+PC9yZWN0Pgo8cmVjdCB4PSIxMCIgeT0iMCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZTVlNWU1Ij48L3JlY3Q+CjxyZWN0IHg9IjAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNlNWU1ZTUiPjwvcmVjdD4KPC9zdmc+)]'
                          : 'bg-gray-100'
                      }`}
                    >
                      <canvas
                        ref={el => {
                          if (el) previewCanvasRefs.current[index] = el;
                        }}
                        className={editState.autoCrop ? "max-w-full max-h-full" : "max-w-full max-h-full object-contain"}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                      {image.processing && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <LoadingIndicator />
                        </div>
                      )}
                      {image.error && (
                        <div className="absolute inset-0 bg-secondary-50 flex items-center justify-center">
                          <p className="text-secondary-700 text-xs p-2 text-center">{image.error}</p>
                        </div>
                      )}
                    </div>
                    <div className="absolute -top-2 -right-2 flex gap-1">
                      {image.processedUrl && !downloadLocked && (
                        <ShareBeforeAfterButton
                          variant="icon"
                          getSources={async () => ({ before: image.originalUrl, after: await renderShareAfter(image) })}
                          appName="AI Background Remover"
                          shareCode="remove-bg"
                          fileName={image.name.replace(/\.[^.]+$/, '')}
                        />
                      )}
                      {!isSingleImage && (
                        <button
                          onClick={() => handlePreview(image)}
                          className="p-1.5 bg-white text-gray-700 rounded-full hover:bg-gray-100 border border-gray-200"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => removeImage(index)}
                        className="p-1.5 bg-secondary-500 text-white rounded-full hover:bg-secondary-600"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => handleImageRotation(index, -1)}
                        className="p-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        title="Rotate -1°"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <div className="flex-1 flex items-center gap-1">
                        <input
                          type="range"
                          min="-180"
                          max="180"
                          value={image.rotation}
                          onChange={(e) => setImages(prev => prev.map((img, i) =>
                            i === index ? { ...img, rotation: Number(e.target.value) } : img
                          ))}
                          className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-700"
                          title={`Rotation: ${image.rotation}°`}
                        />
                        <button
                          onClick={() => handleImageRotationReset(index)}
                          className="text-xs text-gray-500 w-8 text-right hover:text-gray-900 cursor-pointer"
                          title="Click to reset to 0°"
                        >
                          {image.rotation}°
                        </button>
                      </div>
                      <button
                        onClick={() => handleImageRotation(index, 1)}
                        className="p-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        title="Rotate +1°"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-gray-600 truncate">{image.name}</p>
                    {shopifyProductIds.get(image.name)?.productTitle && (
                      <p className="text-xs text-gray-400 truncate flex items-center gap-1" title={shopifyProductIds.get(image.name)!.productTitle}>
                        <Store className="w-3 h-3 flex-shrink-0" />
                        {shopifyProductIds.get(image.name)!.productTitle}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {plan && !plan.has_made_purchase && !isShopifyUser && images.some(img => img.processedBlob) && (
                <div className="mt-6">
                  <WatermarkUpgradeBanner compact />
                </div>
              )}

              <div className="mt-6">
                <div className="flex justify-center gap-4">
                  <button
                    onClick={handleNewImages}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all duration-200 flex items-center gap-2"
                  >
                    <Grid className="w-4 h-4" />
                    New Images
                  </button>

                  <button
                    onClick={handleResetEdits}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all duration-200 flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reset Edits
                  </button>

                  {downloadLocked ? (
                    <LockedDownloadButton />
                  ) : (
                  <button
                    onClick={downloadAll}
                    disabled={downloading}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                      downloading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    {downloading ? (
                      <>
                        <LoadingIndicator size="sm" fullPage={false} />
                        <span>{downloadProgress || 'Processing...'}</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Download All ({images.length} credit{images.length !== 1 ? 's' : ''})
                      </>
                    )}
                  </button>
                  )}

                  {(isAdmin || isShopifyUser) && images.some(img => img.processedBlob) && shopifyProductIds.size > 0 && (
                    <button
                      onClick={async () => {
                        const processedImages = images.filter(img => img.processedBlob && shopifyProductIds.has(img.name));
                        if (processedImages.length === 0) return;

                        if (processedImages.length === 1) {
                          const img = processedImages[0];
                          const canvas = document.createElement('canvas');
                          const imageUrl = editState.removeBackground && img.processedUrl ? img.processedUrl : img.originalUrl;
                          const imageElement = document.createElement('img');
                          await new Promise((resolve, reject) => { imageElement.onload = resolve; imageElement.onerror = reject; imageElement.src = imageUrl; });
                          canvas.width = imageElement.width;
                          canvas.height = imageElement.height;
                          await renderCanvasPreview(canvas, imageUrl, {
                            brightness: editState.brightness, contrast: editState.contrast, saturation: editState.saturation,
                            hue: editState.hue, rotation: img.rotation,
                            backgroundColor: getBackgroundValue(editState.selectedBackground, editState.customColor),
                            shadowType: editState.shadowType, centerProduct: editState.centerProduct,
                            autoCrop: editState.autoCrop, sharpness: editState.sharpness, paddingAmount: editState.paddingAmount,
                          });
                          const blob: Blob = await new Promise((resolve, reject) => {
                            canvas.toBlob(b => b ? resolve(b) : reject(new Error('Failed')), 'image/png');
                          });
                          const reader = new FileReader();
                          const base64 = await new Promise<string>((resolve) => {
                            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
                            reader.readAsDataURL(blob);
                          });
                          const mapping = shopifyProductIds.get(img.name)!;
                          setPushToShopifyImage({
                            base64,
                            productId: mapping.productId,
                            productTitle: mapping.productTitle,
                          });
                        } else {
                          setShopifyPushProgress('Preparing images...');
                          setShowShopifyPushConfirm(true);
                          setShopifyReplaceChoices({});
                          setShopifyProductDetails({});
                          const prepared: typeof shopifyPushImages = [];
                          for (const img of processedImages) {
                            const canvas = document.createElement('canvas');
                            const imageUrl = editState.removeBackground && img.processedUrl ? img.processedUrl : img.originalUrl;
                            const imageElement = document.createElement('img');
                            await new Promise((resolve, reject) => { imageElement.onload = resolve; imageElement.onerror = reject; imageElement.src = imageUrl; });
                            canvas.width = imageElement.width;
                            canvas.height = imageElement.height;
                            await renderCanvasPreview(canvas, imageUrl, {
                              brightness: editState.brightness, contrast: editState.contrast, saturation: editState.saturation,
                              hue: editState.hue, rotation: img.rotation,
                              backgroundColor: getBackgroundValue(editState.selectedBackground, editState.customColor),
                              shadowType: editState.shadowType, centerProduct: editState.centerProduct,
                              autoCrop: editState.autoCrop, sharpness: editState.sharpness, paddingAmount: editState.paddingAmount,
                            });
                            const blob: Blob = await new Promise((resolve, reject) => {
                              canvas.toBlob(b => b ? resolve(b) : reject(new Error('Failed')), 'image/png');
                            });
                            const reader = new FileReader();
                            const base64 = await new Promise<string>((resolve) => {
                              reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
                              reader.readAsDataURL(blob);
                            });
                            const mapping = shopifyProductIds.get(img.name)!;
                            prepared.push({ name: img.name, base64, productId: mapping.productId, productTitle: mapping.productTitle, imageId: mapping.imageId });
                          }
                          setShopifyPushImages(prepared);
                          setShopifyPushProgress('');
                          fetchShopifyProductDetails(prepared);
                        }
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 bg-[#96bf48] text-white hover:bg-[#7aa838]"
                    >
                      <Store className="w-4 h-4" />
                      Push to Shopify
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Right side: Image controls */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-3xl p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Image Settings</h3>

                  {/* Background swatches */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Background</h4>
                    <div className="flex flex-wrap gap-2">
                      {BACKGROUND_SWATCHES.map((swatch) => (
                        <button
                          key={swatch.type}
                          onClick={() => handleBackgroundSelect(swatch.type)}
                          className={`w-8 h-8 rounded-lg border-2 transition-all duration-200 ${
                            editState.selectedBackground === swatch.type
                              ? 'border-gray-900 scale-110'
                              : 'border-gray-200 hover:border-gray-400'
                          } ${swatch.className || ''}`}
                          style={swatch.url
                            ? {
                                backgroundImage: `url(${swatch.url})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }
                            : swatch.color
                              ? { backgroundColor: swatch.color }
                              : undefined}
                          title={swatch.label}
                        >
                          {swatch.icon}
                        </button>
                      ))}
                    </div>

                    {/* Custom URL Input */}
                    {editState.selectedBackground === 'custom_url' && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom Background Image
                        </label>
                        <input
                          type="url"
                          value={editState.customBackgroundUrl}
                          onChange={(e) => updateEditState({ ...editState, customBackgroundUrl: e.target.value })}
                          placeholder="https://images.pexels.com/photos/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Add a link to a custom background image from Pexels or Unsplash. The link should end in jpg or png. <button onClick={() => setIsVideoModalOpen(true)} className="text-primary-600 hover:text-primary-800 underline">See demo</button>
                        </p>
                      </div>
                    )}
                  </div>

                  <ImageControls
                    brightness={editState.brightness}
                    contrast={editState.contrast}
                    saturation={editState.saturation}
                    hue={editState.hue}
                    onBrightnessChange={(value) => updateEditState({ ...editState, brightness: value })}
                    onContrastChange={(value) => updateEditState({ ...editState, contrast: value })}
                    onSaturationChange={(value) => updateEditState({ ...editState, saturation: value })}
                    onHueChange={(value) => updateEditState({ ...editState, hue: value })}
                    shadowType={editState.shadowType}
                    onShadowTypeChange={(type) => updateEditState({ ...editState, shadowType: type })}
                    onCenterProduct={() => updateEditState({ ...editState, centerProduct: !editState.centerProduct })}
                    autoCrop={editState.autoCrop}
                    onAutoCropChange={(enabled) => updateEditState({ ...editState, autoCrop: enabled })}
                    sharpness={editState.sharpness}
                    onSharpnessChange={(value) => updateEditState({ ...editState, sharpness: value })}
                    paddingAmount={editState.paddingAmount}
                    onPaddingAmountChange={(amount) => updateEditState({ ...editState, paddingAmount: amount })}
                  />
                </div>
              </div>
          </div>
        </div>
      )}

      <ColorPickerModal
        isOpen={isColorPickerOpen}
        onClose={() => setIsColorPickerOpen(false)}
        color={editState.customColor}
        onChange={(color) => updateEditState({ ...editState, customColor: color })}
      />

      <PreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={getPreviewImageUrl(previewImage)}
        fileName={previewImage?.name || ''}
        editState={previewImage?.editState}
        currentIndex={previewIndex}
        totalImages={images.length}
        onPrevious={handlePreviewPrevious}
        onNext={handlePreviewNext}
      />

      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoUrl={modalVideoUrl}
        title="Image editor demo"
      />

      <FeedbackWidget appName="bulk_background_remover" showWidget={images.length > 0} />

      <ToolAboutSection html={pageCopy?.about} />
      <ToolDemoVideo video={pageCopy?.video} />
      <ToolFAQ items={pageCopy?.faq} />
      <RelatedTools pageId="background_remover" />

      {showShopifyPushConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => { if (!shopifyPushing) { setShowShopifyPushConfirm(false); setShopifyPushImages([]); setShopifyPushResult(null); } }} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#96bf48] rounded-lg flex items-center justify-center">
                  <Store className="w-4 h-4 text-white" />
                </div>
                <h2 className="font-semibold text-gray-900">Push to Shopify</h2>
              </div>
              {!shopifyPushing && (
                <button
                  onClick={() => { setShowShopifyPushConfirm(false); setShopifyPushImages([]); setShopifyPushResult(null); }}
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {shopifyPushResult ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {shopifyPushResult.failed === 0 ? 'All images pushed' : 'Push completed'}
                  </h3>
                  <p className="text-sm text-gray-500 mb-1">
                    {shopifyPushResult.success} image{shopifyPushResult.success !== 1 ? 's' : ''} pushed successfully.
                  </p>
                  {shopifyPushResult.failed > 0 && (
                    <p className="text-sm text-red-500">
                      {shopifyPushResult.failed} image{shopifyPushResult.failed !== 1 ? 's' : ''} failed.
                    </p>
                  )}
                  <button
                    onClick={() => { setShowShopifyPushConfirm(false); setShopifyPushImages([]); setShopifyPushResult(null); }}
                    className="mt-6 px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Done
                  </button>
                </div>
              ) : shopifyPushing ? (
                <div className="text-center py-8">
                  <Loader className="w-8 h-8 animate-spin text-[#96bf48] mx-auto mb-4" />
                  <p className="text-sm font-medium text-gray-900">{shopifyPushProgress || 'Pushing images...'}</p>
                </div>
              ) : shopifyPushImages.length === 0 ? (
                <div className="text-center py-8">
                  <Loader className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-500">{shopifyPushProgress || 'Preparing images...'}</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-700 mb-3">
                    {shopifyPushImages.length} image{shopifyPushImages.length !== 1 ? 's' : ''} will be pushed to {shopifyPushImages.length === 1 ? 'its' : 'their'} linked Shopify product{shopifyPushImages.length !== 1 ? 's' : ''}.
                  </p>

                  {/* Apply All controls */}
                  {!loadingProductDetails && Object.keys(shopifyProductDetails).length > 0 && (
                    <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-xs font-medium text-gray-600 mr-auto">Apply to all:</span>
                      <button
                        onClick={() => {
                          const choices: Record<number, string | null> = {};
                          shopifyPushImages.forEach((_, i) => { choices[i] = null; });
                          setShopifyReplaceChoices(choices);
                        }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                          shopifyPushImages.every((_, i) => !shopifyReplaceChoices[i])
                            ? 'bg-[#96bf48] text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <ImagePlus className="w-3.5 h-3.5" />
                        Add New
                      </button>
                      <button
                        onClick={() => {
                          const choices: Record<number, string | null> = {};
                          shopifyPushImages.forEach((img, i) => {
                            const details = shopifyProductDetails[img.productId];
                            choices[i] = details?.images?.[0]?.id || null;
                          });
                          setShopifyReplaceChoices(choices);
                        }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                          Object.values(shopifyReplaceChoices).length === shopifyPushImages.length && Object.values(shopifyReplaceChoices).every(v => v !== null && v !== undefined)
                            ? 'bg-[#96bf48] text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Replace
                      </button>
                    </div>
                  )}

                  {loadingProductDetails && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <Loader className="w-4 h-4 animate-spin" />
                      Loading product images...
                    </div>
                  )}

                  <div className="space-y-3 mb-4">
                    {shopifyPushImages.map((img, i) => {
                      const productDetail = shopifyProductDetails[img.productId];
                      const currentChoice = shopifyReplaceChoices[i];
                      const hasImages = productDetail && productDetail.images.length > 0;

                      return (
                        <div key={i} className="rounded-lg border border-gray-200 p-3">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                              <img
                                src={`data:image/png;base64,${img.base64}`}
                                alt=""
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{img.productTitle}</p>
                              <p className="text-xs text-gray-500">
                                {currentChoice ? 'Replacing selected image on product page' : 'Adding new image to product page'}
                              </p>
                            </div>
                          </div>
                          {hasImages && (
                            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                              <button
                                onClick={() => setShopifyReplaceChoices(prev => ({ ...prev, [i]: null }))}
                                className={`w-9 h-9 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                                  !currentChoice ? 'border-[#96bf48] bg-green-50' : 'border-gray-200 hover:border-gray-400'
                                }`}
                                title="Add as new image"
                              >
                                <ImagePlus className="w-3.5 h-3.5 text-gray-500" />
                              </button>
                              {productDetail.images.map((pImg) => (
                                <button
                                  key={pImg.id}
                                  onClick={() => setShopifyReplaceChoices(prev => ({ ...prev, [i]: pImg.id }))}
                                  className={`w-9 h-9 rounded-md border-2 flex-shrink-0 overflow-hidden relative transition-colors ${
                                    currentChoice === pImg.id ? 'border-[#96bf48]' : 'border-gray-200 hover:border-gray-400'
                                  }`}
                                  title="Replace this image"
                                >
                                  <img src={pImg.url} alt="" className="w-full h-full object-cover" />
                                  {currentChoice === pImg.id && (
                                    <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                                      <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {!shopifyPushResult && !shopifyPushing && shopifyPushImages.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3">
                <button
                  onClick={() => { setShowShopifyPushConfirm(false); setShopifyPushImages([]); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMultiShopifyPush}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#96bf48] text-white text-sm font-medium rounded-lg hover:bg-[#7aa838] transition-colors"
                >
                  <Store className="w-4 h-4" />
                  Confirm Push
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {(isAdmin || isShopifyUser) && pushToShopifyImage && (
        <PushToShopifyModal
          isOpen={true}
          onClose={() => setPushToShopifyImage(null)}
          onSuccess={async () => {
            await checkCredits(1);
            refreshPlan();
          }}
          imageBase64={pushToShopifyImage.base64}
          imageUrl={pushToShopifyImage.url}
          defaultProductId={pushToShopifyImage.productId}
          defaultProductTitle={pushToShopifyImage.productTitle}
        />
      )}
    </div>
    </>
  );
}
